import React from 'react';
import { Award, Trophy, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { founderSection, christianeAchievements } from '../data/mock';

const FounderSection = () => {
  const { language } = useLanguage();

  return (
    <section className="py-24 bg-gradient-to-b from-[#0d1f1f] via-[#1a3334] to-[#0d1f1f] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Founder Badge */}
        <div className="text-center mb-12">
          <div className="inline-block">
            <div className="text-sm text-emerald-400 font-bold tracking-widest mb-2">
              {language === 'en' ? founderSection.title_en : founderSection.title_es}
            </div>
            <h2 className="text-6xl md:text-7xl font-bold text-white mb-4">
              {founderSection.name}
            </h2>
            <div className="inline-flex items-center space-x-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-6 py-2">
              <Star className="w-4 h-4 text-emerald-400" fill="currentColor" />
              <span className="text-emerald-300 text-sm font-medium">
                {language === 'en' ? founderSection.subtitle_en : founderSection.subtitle_es}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mt-16">
          {/* Image Section */}
          <div className="relative">
            <div className="relative z-10">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl shadow-emerald-500/20 border-4 border-emerald-500/20">
                <img
                  src={founderSection.image}
                  alt="Christiane Endler"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
            
            {/* Floating Achievement Cards */}
            <div className="absolute -top-8 -right-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-6 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
              <div className="text-white text-center">
                <Trophy className="w-8 h-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">2021</div>
                <div className="text-sm">FIFA Best</div>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl p-6 shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform duration-300">
              <div className="text-white text-center">
                <Award className="w-8 h-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">2022</div>
                <div className="text-sm">UEFA CL</div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="space-y-8">
            {/* Quote */}
            <blockquote className="relative">
              <div className="text-8xl text-emerald-500/20 font-serif absolute -top-8 -left-4">“</div>
              <p className="text-2xl text-gray-300 leading-relaxed italic relative z-10 pl-8">
                {language === 'en' ? founderSection.quote_en : founderSection.quote_es}
              </p>
              <div className="text-8xl text-emerald-500/20 font-serif absolute -bottom-16 right-0">”</div>
            </blockquote>

            <div className="pt-8">
              <div className="text-emerald-400 font-semibold">— {founderSection.quote_author}</div>
            </div>

            {/* Achievements Grid */}
            <div className="grid grid-cols-2 gap-4 pt-8">
              {christianeAchievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-emerald-500/50 hover:bg-gray-800/70 transition-all duration-300 group cursor-pointer"
                >
                  <div className="text-emerald-400 text-3xl font-bold mb-2 group-hover:scale-110 transition-transform">
                    {achievement.year}
                  </div>
                  <div className="text-white font-semibold text-sm mb-1">
                    {language === 'en' ? achievement.title_en : achievement.title_es}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {language === 'en' ? achievement.description_en : achievement.description_es}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;